import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { api, errMsg } from "@/lib/api";
import { DURATION, STYLE, FORMAT, STAGES, egp, fmtDate, cardCls, orderLabel } from "@/lib/labels";
import { StageTracker, StageBar } from "@/components/account/StageTracker";
import { OrderReviews } from "@/components/account/OrderReviews";

export default function OrderDetails() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = () => api.get(`/customer/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => setError(errMsg(e, "الأوردر مش موجود")));
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [id]);

  if (error) return <p className="px-4 py-24 text-center text-red-300" data-testid="order-error">{error}</p>;
  if (!order) return <p className="px-4 py-24 text-center text-purple-300/70">بنحمّل...</p>;
  const cancelled = order.status === "cancelled";
  const rows = [["المدة", DURATION[order.duration]], ["الأسلوب", STYLE[order.style]], ["الفورمات", FORMAT[order.format]],
    ["السعر", order.is_free ? "مجاني (مكافأة)" : egp(order.price)], ["تاريخ الطلب", fmtDate(order.created_at)]];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="order-details-page">
      <Seo title={orderLabel(order)} noindex />
      <div className="mb-6">
        <Link to="/account" className="inline-flex items-center gap-1 text-[var(--km-lilac)] hover:text-white" data-testid="order-back-link"><ArrowRight className="h-4 w-4" /> حسابي</Link>
      </div>
      <h1 className="font-display text-4xl text-white sm:text-5xl" data-testid="order-title">{orderLabel(order)}</h1>
      <p className={`font-head mt-3 text-xl font-bold ${cancelled ? "text-red-300" : "text-[var(--km-cyan)]"}`} data-testid="order-current-stage">
        {cancelled ? `ملغي${order.cancel_reason ? `: ${order.cancel_reason}` : ""}` : STAGES[order.stage]}
      </p>
      {cancelled && order.refund_percent !== undefined && (
        <p className="mt-2 text-sm text-red-200" data-testid="order-refund-info">
          {order.is_free ? (order.refund_percent === 100 ? "اتلغى ورجعتلك مكافأة الإعلان المجاني" : "اتلغى من غير ما المكافأة ترجع") : order.refund_percent > 0 ? `اترجعلك ${egp(order.refund_amount ?? order.price)} (${order.refund_percent}%) لمحفظتك على الموقع` : "الأوردر اتلغى من غير استرداد"} · {fmtDate(order.cancelled_at)}
        </p>
      )}
      <div className="mt-4"><StageBar stage={order.stage} cancelled={cancelled} /></div>
      <section className={`${cardCls} mt-8 p-6`}>
        <h2 className="font-head mb-5 text-lg font-bold text-white">مراحل الإنتاج</h2>
        <StageTracker stage={order.stage} history={order.stage_history} cancelled={cancelled} />
      </section>
      <OrderReviews order={order} onChange={setOrder} />
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className={`${cardCls} p-6`}>
          <h2 className="font-head mb-4 text-lg font-bold text-white">تفاصيل الإعلان</h2>
          <dl className="space-y-2">
            {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3 border-b border-purple-500/10 pb-2"><dt className="text-purple-300/70">{k}</dt><dd className="font-head font-bold text-white">{v}</dd></div>)}
          </dl>
          <p className="font-head mt-4 text-sm font-bold text-[var(--km-cyan)]">أهم النقاط الإعلانية</p>
          <p className="whitespace-pre-wrap break-words text-purple-100/90">{order.key_points}</p>
          {order.notes && <><p className="font-head mt-3 text-sm font-bold text-[var(--km-cyan)]">ملاحظات</p><p className="whitespace-pre-wrap break-words text-purple-100/90">{order.notes}</p></>}
        </section>
        <section className={`${cardCls} p-6`}>
          <h2 className="font-head mb-4 text-lg font-bold text-white">سجل التحديثات</h2>
          <ol className="space-y-3" data-testid="order-history">
            {[...order.stage_history].reverse().map((h, i) => (
              <li key={i} className="flex justify-between gap-3 rounded-2xl bg-black/30 p-3">
                <span className="font-head text-sm font-bold text-white">{STAGES[h.stage]}</span>
                <span className="text-xs text-purple-300/70">{fmtDate(h.at)}</span>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}
